// src/pages/ParkingSpotDetail.tsx
import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { addDays } from "date-fns";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import Header from "@/components/Header";
import GeneralChatDrawer from "@/components/chat/GeneralChatDrawer";
import placeholder from "@/assets/listing-placeholder.svg";
import { FavoriteButton } from "@/components/FavoriteButton";

import { supabase } from "@/integrations/supabase/client";
import { expandAvailability, dayHasAvailability } from "@/lib/availability";
import type { Interval } from "@/lib/availability";
import AvailabilityChips from "@/components/AvailabilityChips";
import ReviewsSection from "@/components/ReviewsSection";
import UserRating from "@/components/UserRating";
import { useToast } from "@/hooks/use-toast";
import Map from "@/components/Map";
import { maskAddressForMap } from "@/lib/addressMasking";
import { useHasConfirmedBooking } from "@/hooks/useHasConfirmedBooking";
import { SEOHead } from "@/components/SEOHead";


import {
  ArrowLeft,
  Star,
  Zap,
  Shield,
  Car,
  MapPin,
  Ruler,
  MessageSquare,
  Share,
  Heart,
  Camera,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
} from "lucide-react";

type DbListing = {
  id: string;
  owner_id: string;
  title: string;
  address: string;
  description: string | null;
  rules: string | null;
  size: "S" | "M" | "L" | "XL";
  facilities: any | null;
  photos: string[] | null;
  base_price_day: number | null;   // øre
  base_price_hour: number | null;  // øre
  visibility: boolean;
  geo_lat: number | null;
  geo_lng: number | null;
};

type DbUser = {
  id: string;
  name: string | null;
  photo_url: string | null;
  verified_phone_at: string | null;
  created_at: string;
};

export default function ParkingSpotDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const listingId = id!;

  // UI-state
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [bookingDays, setBookingDays] = useState(1);
  const [isLiked, setIsLiked] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const { toast } = useToast();

  // Get current user
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data.user?.id || null);
    });
  }, []);

  // Check if current user has confirmed booking (must be before early returns!)
  const hasBookingQ = useHasConfirmedBooking(listingId, currentUserId);

  // --- Hent listing + vært ---
  const listingQ = useQuery({
    queryKey: ["listing", listingId],
    queryFn: async (): Promise<{ listing: DbListing; owner: DbUser | null }> => {
      const { data: l, error } = await supabase
        .from("listings")
        .select(
          "id,owner_id,title,address,description,rules,size,facilities,photos,base_price_day,base_price_hour,visibility,geo_lat,geo_lng"
        )
        .eq("id", listingId)
        .maybeSingle();

      if (error) throw error;
      if (!l) throw new Error("Listing not found");

      const { data: owner } = await supabase
        .from("users")
        .select("id,name,photo_url,verified_phone_at,created_at")
        .eq("id", (l as DbListing).owner_id)
        .maybeSingle();

      return { listing: l as DbListing, owner: (owner as DbUser) ?? null };
    },
  });

  // --- Availability til kalender (90 dage frem) ---
  const rangeStart = new Date();
  const rangeEnd = addDays(rangeStart, 90);

  const availabilityQ = useQuery({
    enabled: !!listingId,
    queryKey: ["availability", listingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("availability")
        .select("*")
        .eq("listing_id", listingId);
      if (error) throw error;
      return data ?? [];
    },
  });

  // --- Hent eksisterende bookings for at disable optagne datoer ---
  const bookingsQ = useQuery({
    enabled: !!listingId,
    queryKey: ["bookings", listingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("start_at, end_at")
        .eq("listing_id", listingId)
        .in("status", ["confirmed", "pending"]);
      if (error) throw error;
      return data ?? [];
    },
  });

  const windows: Interval[] = useMemo(() => {
    if (!availabilityQ.data) return [];
    return expandAvailability(availabilityQ.data as any, rangeStart, rangeEnd);
  }, [availabilityQ.data]);

  const hasAvailabilityRows = !!availabilityQ.data?.length;

  // Loading / error
  if (listingQ.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-6 max-w-7xl">Henter…</div>
      </div>
    );
  }
  if (listingQ.isError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-6 max-w-7xl text-red-600">
          Kunne ikke hente parkeringspladsen.
        </div>
      </div>
    );
  }

  const db = listingQ.data?.listing;
  const owner = listingQ.data?.owner;

  // Computed values (using hasBookingQ that was called at the top)
  const isOwner = currentUserId === db?.owner_id;
  const canSeeFullAddress = isOwner || hasBookingQ.data;

  // Early return if no data
  if (!db) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-6 max-w-7xl">Henter…</div>
      </div>
    );
  }

  // Afledte værdier
  const photos: string[] =
    Array.isArray(db.photos) && db.photos.length ? db.photos : [placeholder];

  const priceDayKr = Math.max(0, Math.round((db.base_price_day ?? 0) / 100));
  const platformFeePct = 15;
  const totalPrice = priceDayKr * bookingDays;
  // Platform fee is deducted from host's earnings, not added to renter's price
  const hostEarnings = Math.round(totalPrice * (100 - platformFeePct) / 100);

  const sizeLabel: Record<DbListing["size"], string> = {
    S: "Lille",
    M: "Medium",
    L: "Stor",
    XL: "Ekstra Stor",
  };

  const sizeTooltip: Record<DbListing["size"], string> = {
    S: "Passer til små biler (Smart, Fiat 500)",
    M: "Passer til mellemstor bil (VW Golf, Toyota Corolla)",
    L: "Passer til stor bil (BMW 5-serie, Volvo XC90)",
    XL: "Passer til ekstra stor bil eller varevogn",
  };

  const nextPhoto = () =>
    setCurrentPhotoIndex((i) => (i === photos.length - 1 ? 0 : i + 1));
  const prevPhoto = () =>
    setCurrentPhotoIndex((i) => (i === 0 ? photos.length - 1 : i - 1));

  const ownerInitials =
    (owner?.name || "Bruger")
      .split(" ")
      .map((n) => n[0])
      .join("") || "US";

  const memberSince = owner?.created_at
    ? new Date(owner.created_at).getFullYear()
    : undefined;

  // Display address: full if owner/confirmed booking, masked otherwise
  const displayAddress = canSeeFullAddress ? db.address : maskAddressForMap(db.address);
  const seoTitle = `${db.title} | Privat parkering fra ${priceDayKr} kr/dag`;
  const seoDescription = `Book privat parkering ved ${maskAddressForMap(db.address)}. ${sizeLabel[db.size]} plads fra ${priceDayKr} kr. pr. dag på SharePark.`;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={`parkering ${maskAddressForMap(db.address)}, privat parkering, lej parkeringsplads, ${db.title}`}
        image={photos[0]}
        url={`https://sharepark.dk/spot/${db.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: db.title,
            description: seoDescription,
            image: photos[0],
            offers: {
              "@type": "Offer",
              price: priceDayKr,
              priceCurrency: "DKK",
              availability: hasAvailabilityRows ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              url: `https://sharepark.dk/spot/${db.id}`,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Hvordan booker jeg denne parkeringsplads?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Vælg ledige datoer i kalenderen, fortsæt til booking og gennemfør betalingen. Bookingen oprettes, når betalingen er verificeret.",
                },
              },
              {
                "@type": "Question",
                name: "Kan jeg afbestille denne parkeringsplads?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Ja, afbestilling følger den afbestillingspolitik, som er knyttet til parkeringspladsen. Eventuel refusion afhænger af tidspunktet for afbestilling.",
                },
              },
              {
                "@type": "Question",
                name: "Hvornår vises den fulde adresse?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Den fulde adresse og husnummer vises først for lejeren, når der er en bekræftet booking. Før booking vises kun en maskeret adresse.",
                },
              },
            ],
          },
        ]}
      />
      <Header />

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Back button */}
        <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Tilbage
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo gallery */}
            <div className="space-y-4">
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                <img
                  src={photos[currentPhotoIndex]}
                  alt={`${db.title} - billede ${currentPhotoIndex + 1}`}
                  className="w-full h-full object-cover"
                />

                {photos.length > 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-background/80 backdrop-blur-sm"
                      onClick={prevPhoto}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-background/80 backdrop-blur-sm"
                      onClick={nextPhoto}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>

                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                      <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm">
                        <Camera className="h-3 w-3 mr-1" />
                        {currentPhotoIndex + 1} / {photos.length}
                      </Badge>
                    </div>
                  </>
                )}

                <div className="absolute top-4 right-4 flex gap-2">
                  <Button variant="secondary" size="icon" className="bg-background/80 backdrop-blur-sm">
                    <Share className="h-4 w-4" />
                  </Button>

                  <FavoriteButton listingId={listingId} variant="default" />
                </div>
              </div>

              {/* Thumbnails */}
              {photos.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {photos.map((photo, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPhotoIndex(index)}
                      className={`flex-shrink-0 w-20 h-16 rounded-md overflow-hidden border-2 ${
                        index === currentPhotoIndex
                          ? "border-primary"
                          : "border-transparent"
                      }`}
                    >
                      <img
                        src={photo}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Title and basic info */}
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold text-foreground">
                    {db.title}
                  </h1>

                  <div className="flex items-center text-muted-foreground">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span className="text-sm">{displayAddress}</span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold text-foreground">
                    {priceDayKr} kr
                  </div>
                  <div className="text-sm text-muted-foreground">pr. dag</div>
                </div>
              </div>

              {/* Rating and features */}
              <div className="flex flex-wrap items-center gap-4">
                <UserRating 
                  userId={db.owner_id} 
                  currentUserId={currentUserId}
                  showCount
                />

                <div className="flex flex-wrap items-center gap-4">
  {/* dine eksisterende badges for rating/størrelse/charger */}
  <AvailabilityChips windows={windows} hasRows={hasAvailabilityRows} />
</div>

                <TooltipProvider>
                  <div className="flex gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="cursor-help">
                          <Car className="h-3 w-3 mr-1" />
                          {sizeLabel[db.size]}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{sizeTooltip[db.size]}</p>
                      </TooltipContent>
                    </Tooltip>

                    {db?.facilities?.charger && (
                      <Badge variant="secondary" className="bg-success text-success-foreground">
                        <Zap className="h-3 w-3 mr-1" />
                        El-lader
                      </Badge>
                    )}

                    {db?.facilities?.covered && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="secondary"
                            className="bg-primary text-primary-foreground cursor-help"
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            Overdækket
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Tør bil – mindre sne, is og regnvejr</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              </div>
            </div>

            <Separator />

            {/* Host info */}
            <div className="flex items-center space-x-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={owner?.photo_url || undefined} />
                <AvatarFallback>{ownerInitials}</AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-foreground">
                    {owner?.name || "Vært"}
                  </h3>
                  {owner?.verified_phone_at && (
                    <CheckCircle className="h-4 w-4 text-success" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <UserRating 
                    userId={db.owner_id} 
                    currentUserId={currentUserId || undefined} 
                    size="sm" 
                  />
                  {memberSince && <span>• Medlem siden {memberSince}</span>}
                </div>
              </div>

              <Button 
                variant="outline" 
                size="sm"
                onClick={(e) => {
                  // Blur trigger/evt. aktivt element for at undgå fokus-konflikt når aria-hidden sættes
                  e.currentTarget.blur();
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                  if (!currentUserId) {
                    toast({
                      title: "Log ind påkrævet",
                      description: "Du skal være logget ind for at kontakte værten",
                      variant: "destructive",
                    });
                    navigate('/auth');
                    return;
                  }
                  if (currentUserId === db.owner_id) {
                    toast({
                      title: "Du kan ikke kontakte dig selv",
                      description: "Dette er dit eget opslag",
                    });
                    return;
                  }
                  setChatOpen(true);
                }}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Skriv til værten
              </Button>
            </div>

            <Separator />

            {/* Description */}
            {db.description && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Om denne parkeringsplads</h2>
                <p className="text-foreground leading-relaxed">{db.description}</p>
              </div>
            )}

            {/* Details */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Detaljer</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Ruler className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>Maks. højde: {/* tilføj felt i DB hvis nødvendigt */}2.0m</span>
                </div>

                {db?.facilities?.gate && (
                  <div className="flex items-center">
                    <Shield className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>Låge/port</span>
                  </div>
                )}
              </div>
            </div>

            {/* Rules */}
            {db.rules && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Regler</h2>
                <p className="text-foreground">{db.rules}</p>
              </div>
            )}

            {/* Location Map */}
            {db.geo_lat && db.geo_lng && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Lokation</h2>
                <div className="rounded-lg overflow-hidden border">
                  <Map 
                    listings={[{
                      id: db.id,
                      title: db.title,
                      address: displayAddress,
                      geo_lat: db.geo_lat,
                      geo_lng: db.geo_lng,
                      base_price_day: db.base_price_day,
                    }]}
                    center={[db.geo_lng, db.geo_lat]}
                    zoom={14}
                    height="400px"
                  />
                </div>
              </div>
            )}

            {/* Reviews */}
            <ReviewsSection listingId={listingId} />
          </div>

          {/* Booking sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Book denne plads</CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="text-sm text-muted-foreground">Vælg datoer</div>

                    <Calendar
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={(dates) => {
                        setSelectedDates(dates || []);
                        setBookingDays(dates?.length || 1);
                      }}
                      disabled={(date) => {
                        // Disable fortid
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;

                        // Tjek om datoen er allerede booket
                        if (bookingsQ.data) {
                          const dateTime = date.getTime();
                          const isBooked = bookingsQ.data.some((booking) => {
                            const start = new Date(booking.start_at).getTime();
                            const end = new Date(booking.end_at).getTime();
                            return dateTime >= start && dateTime <= end;
                          });
                          if (isBooked) return true;
                        }

                        // Ingen availability-rækker → tillad alle fremtidige dage
                        if (!hasAvailabilityRows) return false;

                        // Kun dage, der er dækket af et availability-vindue
                        return !dayHasAvailability(date, windows);
                      }}
                      className="rounded-md border"
                    />
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>
                        {priceDayKr} kr × {bookingDays} dag
                        {bookingDays !== 1 ? "e" : ""}
                      </span>
                      <span>{totalPrice} kr</span>
                    </div>

                    <Separator />

                    <div className="flex justify-between font-semibold">
                      <span>Total</span>
                      <span>{totalPrice} kr</span>
                    </div>
                    
                    <div className="text-xs text-muted-foreground">
                      Inkl. {platformFeePct}% platform gebyr som værten betaler
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={() =>
                      navigate(`/book/${listingId}`, {
                        state: { listing: db, selectedDates, totalPrice, bookingDays },
                      })
                    }
                    disabled={selectedDates.length === 0}
                  >
                    Book nu
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Sikker betaling via Stripe. Fuld adresse deles efter bekræftet booking.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 p-3 shadow-lg backdrop-blur lg:hidden">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{priceDayKr} kr pr. dag</div>
            <div className="truncate text-xs text-muted-foreground">
              {selectedDates.length ? `${selectedDates.length} dag${selectedDates.length > 1 ? "e" : ""} valgt` : "Vælg datoer for at booke"}
            </div>
          </div>
          <Button
            onClick={() =>
              navigate(`/book/${listingId}`, {
                state: { listing: db, selectedDates, totalPrice, bookingDays },
              })
            }
            disabled={selectedDates.length === 0}
          >
            Book nu
          </Button>
        </div>
      </div>

      {/* Chat Drawer */}
      {currentUserId && db && (
        <GeneralChatDrawer
          open={chatOpen}
          onOpenChange={setChatOpen}
          listingId={listingId}
          listingTitle={db.title}
          hostId={db.owner_id}
          hostName={owner?.name || undefined}
          hostAvatarUrl={owner?.photo_url || undefined}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
}
